import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { mediaQueries, mixins, textStyles } from '#@/shared/utils/styles';

import Clickable from '../Clickable';
import ExternalLink from '../ExternalLink';
import LogoSvg from './logo.svg?react';
import type { MainNavigationTheme } from './theme';

type PropsWithTheme<T = unknown> = T & { theme: MainNavigationTheme };

export const CONTAINER_WIDTH_COLLAPSED = 68;
export const CONTAINER_WIDTH_EXPANDED = 224;
export const CONTAINER_WIDTH_TRANSITION_TIME = 200;

const LOGO_HEIGHT = 28;
const LOGO_LINK_HEIGHT = LOGO_HEIGHT + 8 * 2;
const LOGO_LINK_OFFSET_TOP = 24;
const BUTTON_HEIGHT = 52;
const BUTTON_MARGIN = 2;
const COLLAPSE_BUTTON_OFFSET_BOTTOM = 24;

export const Container = styled.div<PropsWithTheme<{ $isCollapsed: boolean }>>`
  flex-shrink: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${(props) =>
    props.$isCollapsed ? CONTAINER_WIDTH_COLLAPSED : CONTAINER_WIDTH_EXPANDED}px;
  height: 100vh;
  max-height: 100%;
  background: ${(props) => props.theme.mainNavigationBackgroundColor};
  transition: width ${CONTAINER_WIDTH_TRANSITION_TIME}ms cubic-bezier(0.83, 0, 0.17, 1);
  overflow: hidden;

  @media (${mediaQueries.mobile}) {
    width: ${(props) => (props.$isCollapsed ? '0' : '100%')};
  }
`;

export const LogoContainer = styled.div<{ $isCustomLogo: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-between;
  top: ${LOGO_LINK_OFFSET_TOP}px;
  width: 100%;
  padding: ${(props) => (props.$isCustomLogo ? '0 25px 0 10px' : '0 25px')};
`;

export const LogoLink = styled(NavLink)`
  display: flex;
  align-items: center;
  height: ${LOGO_LINK_HEIGHT}px;
`;

export const Logo = styled(LogoSvg)<{ $isCollapsed: boolean }>`
  --clip-height: ${LOGO_HEIGHT}px;

  height: ${LOGO_HEIGHT}px;

  ${(props) =>
    props.$isCollapsed &&
    css`
      clip-path: polygon(
        0 0,
        var(--clip-height) 0,
        var(--clip-height) var(--clip-height),
        0 var(--clip-height)
      );
    `}
`;

export const CustomLogo = styled.img`
  max-width: 55px;
  max-height: 220px;
`;

export const NavigationItemsWrapper = styled.div<
  PropsWithTheme<{ $showScrollbar: boolean; $collapsible: boolean }>
>`
  --topOffset: ${LOGO_LINK_HEIGHT + LOGO_LINK_OFFSET_TOP}px;
  --bottomOffset: ${({ $collapsible }) =>
    $collapsible ? BUTTON_HEIGHT + COLLAPSE_BUTTON_OFFSET_BOTTOM + 2 * BUTTON_MARGIN : 0}px;

  position: relative;
  top: var(--topOffset);
  bottom: var(--bottomOffset);
  display: flex;
  flex-direction: column;
  height: calc(100% - var(--topOffset) - var(--bottomOffset));
  padding: 24px 0;
  overflow-y: auto;
  overflow-x: hidden;

  ${(props) =>
    props.$showScrollbar
      ? css`
          --thumb-width: 8px;
          --thumb-offset: 3px;
          --color: ${mixins.rgba(props.theme.mainNavigationTextColor, 0.2)};
          --colorHover: ${mixins.rgba(props.theme.mainNavigationTextColor, 0.3)};

          /* There appears to be a bug in Blink-based browsers where if
          scrollbar-color is set, pseudo-elements from the ::-webkit-scrollbar
          family won't apply anymore. On those browsers, we don't need
          scrollbar-color at all. However, it is needed for Firefox. */
          @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: thin;
            scrollbar-color: var(--color) transparent;

            &:hover {
              scrollbar-color: var(--colorHover) transparent;
            }
          }

          &::-webkit-scrollbar {
            width: calc(var(--thumb-width) + 2 * var(--thumb-offset));
          }

          &::-webkit-scrollbar-track {
            background-color: transparent;
          }

          &::-webkit-scrollbar-thumb {
            border: var(--thumb-offset) solid transparent;
            background-clip: padding-box;
            background-color: var(--color);
            border-radius: 7px;
          }

          &:hover::-webkit-scrollbar-thumb {
            background-color: var(--colorHover);
          }
        `
      : css`
          scrollbar-width: none;

          &::-webkit-scrollbar {
            width: 0;
          }
        `}
`;

const buttonStyles = (props: PropsWithTheme) => css`
  --textColor: ${mixins.rgba(props.theme.mainNavigationTextColor, 0.8)};
  --textColorActive: ${props.theme.mainNavigationTextColor};

  display: flex;
  align-items: center;
  gap: 16px;
  height: ${BUTTON_HEIGHT}px;
  margin: ${BUTTON_MARGIN}px 8px;
  border-radius: 8px;
  padding: 16px;
  ${textStyles.labelLarge450};
  color: var(--textColor);

  &:hover {
    background-color: ${mixins.rgba(props.theme.mainNavigationTextColor, 0.05)};
    color: var(--textColorActive);
  }
  &:active {
    background-color: ${mixins.rgba(props.theme.mainNavigationTextColor, 0.1)};
  }
`;

export const NavButton = styled(NavLink)<PropsWithTheme>`
  ${buttonStyles}

  --bgColor: ${(props) => mixins.rgba(props.theme.mainNavigationTextColor, 0.15)};

  /* Class name is from react-router */
  &.active {
    background-color: var(--bgColor);
    color: var(--textColorActive);

    &:hover {
      background-color: var(--bgColor);
    }

    &::before {
      content: '';
      position: absolute;
      width: 4px;
      height: 24px;
      left: 0px;
      background-color: ${(props) => props.theme.mainNavigationTextColor};
      border-radius: 0px 20px 20px 0px;
    }
  }
`;

export const NavExternalButton = styled(ExternalLink)`
  ${buttonStyles}

  &,
  &:focus,
  &:visited {
    color: var(--textColor);
  }

  &:hover,
  &:active {
    color: var(--textColorActive);
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

export const Divider = styled.hr<PropsWithTheme>`
  height: 0;
  border: 0;
  border-top: 1px solid ${(props) => mixins.rgba(props.theme.mainNavigationTextColor, 0.1)};
  margin: 8px 20px;
`;

export const CollapseButton = styled(Clickable)`
  ${buttonStyles}

  position: absolute;
  bottom: ${COLLAPSE_BUTTON_OFFSET_BOTTOM}px;
  left: 0;
  right: 0;
`;

export const ButtonText = styled.span<{ $isCollapsed: boolean }>`
  white-space: nowrap;
  transition: opacity 0.2s cubic-bezier(0.83, 0, 0.17, 1);

  ${(props) =>
    props.$isCollapsed &&
    css`
      opacity: 0;
    `}
`;
